import { default as React } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { TaskPage } from '@backstage/plugin-scaffolder';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  DeploymentFailureAlert,
  isTerminalTaskFailure,
} from './DeploymentFailureAlert';
import { linkifyDomElement } from './linkifyText';

/**
 * Resolves the Backstage `Content` area of the rendered task page so the failure
 * alert can be injected as its first child — immediately below the page header
 * and above the stepper component, visible without scrolling.
 *
 * Backstage's `Content` renders an element whose class contains `contentWrapper`
 * (the OngoingTask makeStyles key) on some versions; when that is absent we
 * target the `Content` element itself, which Backstage renders as the
 * `main > article` grid cell (grid-area `pageContent`) that holds the stepper
 * and logs. We deliberately do NOT fall back to `<main>`: `<main>` is the page
 * CSS grid container, and injecting an unplaced child there steals a grid column
 * and collapses the stepper/logs to a sliver (IDP-996 regression). `<main>`
 * remains only as a last resort, and the host below is forced to span the full
 * grid width so it can never squeeze the content column.
 */
function findContentWrapper(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>('[class*="contentWrapper"]') ??
    document.querySelector<HTMLElement>('main > article') ??
    document.querySelector<HTMLElement>('main')
  );
}

/**
 * Finds the "Helpful Links" heading inside the rendered Deployment Status
 * markdown and returns the block-level element immediately before which the
 * pod-log terminal should be inserted.
 *
 * The markdown renders "**🔗 Helpful Links:**" as a <p> or a <p><strong>
 * child. We locate either form and return the <p> ancestor so the caller
 * can use parentElement.insertBefore(host, target).
 */
function findHelpfulLinksAnchor(): HTMLElement | null {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>('p, strong'),
  );
  const match = candidates.find(el =>
    el.textContent?.includes('Helpful Links'),
  );
  if (!match) return null;
  // If we matched a <strong> inside a <p>, return the <p>.
  return match.tagName === 'P'
    ? match
    : (match.closest('p') as HTMLElement | null) ?? match;
}

/**
 * Custom scaffolder task page that renders a prominent error alert at the top of
 * the page content when a deployment fails, then renders the default Backstage
 * task page (stepper, logs, outputs) unchanged.
 *
 * When the ArgoCD deployment fails, a dark terminal block containing the pod
 * crash logs is injected inline inside the Deployment Status output section —
 * immediately before the "Helpful Links" list — so it appears in context rather
 * than at the bottom of the page.
 *
 * Wired into the app via the `components.TaskPageComponent` prop on
 * `ScaffolderPage` in App.tsx.
 *
 * Implements IDP-996, IDP-1109.
 */
export const CustomTaskPage = () => {
  const { taskId } = useParams();
  const taskStream = useTaskEventStream(taskId ?? '');

  // Only inject the alert for a terminal failure — never for a transient network
  // error while the task is still running (IDP-996 false-positive fix).
  const hasFailed = isTerminalTaskFailure(taskStream);

  const [alertHost, setAlertHost] = useState<HTMLElement | null>(null);
  const [terminalHost, setTerminalHost] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);

  // Extract pod logs for the inline terminal block.
  // Primary: direct output field set by ctx.output() in the action.
  // Fallback: parse from the deployment-status markdown (code-fence extraction).
  const [podLogsContent, podNameOutput] = useMemo<
    [string | undefined, string | undefined]
  >(() => {
    if (!hasFailed) return [undefined, undefined];
    const direct = taskStream.output?.podLogsContent as string | undefined;
    if (direct) {
      return [direct, taskStream.output?.podNameOutput as string | undefined];
    }
    const textOutput = taskStream.output?.text as
      | Array<{ title: string; content: string }>
      | undefined;
    const md = textOutput?.[0]?.content ?? '';
    // Use [^*]* (not [^:]*) so the pattern tolerates the colon inside "(pod: X)".
    const logsMatch = md.match(
      /\*\*Pod Logs[^*]*:\*\*\n\n```\n([\s\S]*?)\n```/,
    );
    if (logsMatch) {
      const nameMatch = md.match(/\(pod: ([^)]+)\)/);
      return [logsMatch[1], nameMatch?.[1]];
    }
    return [undefined, undefined];
  }, [hasFailed, taskStream.output]);

  const handleCopyLogs = () => {
    if (!podLogsContent) return;
    navigator.clipboard.writeText(podLogsContent).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Once a failure is detected, find the content wrapper and create a host
  // element as its first child to portal the alert into. Retries briefly while
  // the task page mounts.
  useEffect(() => {
    if (!hasFailed) {
      setAlertHost(null);
      return undefined;
    }

    let host: HTMLDivElement | null = null;
    let attempts = 0;

    const attach = (): boolean => {
      const wrapper = findContentWrapper();
      if (!wrapper) {
        return false;
      }
      host = document.createElement('div');
      host.setAttribute('data-testid', 'deployment-failure-alert-host');
      // Defensive: if the resolved wrapper is the page CSS grid (`<main>`),
      // span every column so the host occupies a full-width row instead of
      // claiming an auto-placed column and squeezing the stepper/logs.
      host.style.gridColumn = '1 / -1';
      host.style.width = '100%';
      wrapper.insertBefore(host, wrapper.firstChild);
      setAlertHost(host);
      return true;
    };

    if (attach()) {
      // Attached on first try.
    } else {
      const intervalId = setInterval(() => {
        attempts += 1;
        if (attach() || attempts >= 20) {
          clearInterval(intervalId);
        }
      }, 100);
      return () => {
        clearInterval(intervalId);
        host?.remove();
      };
    }

    return () => {
      host?.remove();
    };
  }, [hasFailed]);

  // Inject the terminal block host immediately before "Helpful Links:" inside
  // the rendered Deployment Status markdown. This positions the dark terminal
  // panel in context — where the plain-text pod logs used to appear — rather
  // than at the bottom of the page. Retries until the markdown section mounts.
  useEffect(() => {
    if (!hasFailed || !podLogsContent) {
      setTerminalHost(null);
      return undefined;
    }

    let host: HTMLDivElement | null = null;
    let attempts = 0;

    const attach = (): boolean => {
      const anchor = findHelpfulLinksAnchor();
      if (!anchor || !anchor.parentElement) return false;
      host = document.createElement('div');
      host.setAttribute('data-testid', 'pod-logs-terminal-host');
      anchor.parentElement.insertBefore(host, anchor);
      setTerminalHost(host);
      return true;
    };

    if (!attach()) {
      const intervalId = setInterval(() => {
        attempts += 1;
        if (attach() || attempts >= 30) {
          clearInterval(intervalId);
        }
      }, 150);
      return () => {
        clearInterval(intervalId);
        host?.remove();
      };
    }

    return () => {
      host?.remove();
    };
  }, [hasFailed, podLogsContent]);

  // Linkify URLs inside Backstage's built-in log viewer. The log DOM is owned
  // by the TaskPage component so we cannot control it via React — instead we
  // observe mutations and post-process new text nodes that contain URLs.
  // Batches rapid mutations into a single scan using Set deduplication.
  useEffect(() => {
    const observer = new MutationObserver(mutations => {
      const targets = new Set<Element>();
      for (const mutation of mutations) {
        const target =
          mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;
        if (target) {
          targets.add(target);
        }
      }
      targets.forEach(t => linkifyDomElement(t));
    });

    // Wait briefly for the TaskPage to mount its log elements.
    const timerId = setTimeout(() => {
      const main = document.querySelector('main');
      if (main) {
        linkifyDomElement(main);
        observer.observe(main, {
          childList: true,
          subtree: true,
        });
      }
    }, 500);

    return () => {
      clearTimeout(timerId);
      observer.disconnect();
    };
  }, []);

  return (
    <div data-skystage-task-page>
      {alertHost &&
        createPortal(
          <DeploymentFailureAlert
            taskStream={taskStream}
            startOverUrl="/create"
          />,
          alertHost,
        )}
      <TaskPage />
      {terminalHost &&
        createPortal(
          <Box sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
              {podNameOutput
                ? `Pod Logs — last 30 lines (pod: ${podNameOutput})`
                : 'Pod Logs — last 30 lines'}
            </Typography>
            <Box
              sx={{ bgcolor: '#1e1e2e', borderRadius: 1, overflow: 'hidden' }}
            >
              {/* Header bar */}
              <Box
                sx={{
                  bgcolor: '#2a2a3e',
                  px: 2,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#888', fontFamily: 'monospace' }}
                >
                  Terminal
                </Typography>
                <Tooltip
                  title={copied ? 'Copied!' : 'Copy logs'}
                  placement="left"
                >
                  <Button
                    size="small"
                    onClick={handleCopyLogs}
                    startIcon={
                      <ContentCopyIcon sx={{ fontSize: '14px !important' }} />
                    }
                    sx={{
                      color: '#98c379',
                      fontSize: 11,
                      textTransform: 'none',
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      border: '1px solid rgba(152,195,121,0.4)',
                      borderRadius: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(152,195,121,0.1)',
                        border: '1px solid rgba(152,195,121,0.8)',
                      },
                    }}
                    aria-label="Copy pod logs"
                  >
                    Copy
                  </Button>
                </Tooltip>
              </Box>
              {/* Log content */}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  fontFamily:
                    '"Cascadia Code", "Fira Code", Consolas, "Courier New", monospace',
                  fontSize: 12,
                  color: '#e6edf3',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.55,
                  overflowX: 'auto',
                  maxHeight: 400,
                  overflowY: 'auto',
                }}
              >
                {podLogsContent}
              </Box>
            </Box>
          </Box>,
          terminalHost,
        )}
    </div>
  );
};
